import React, { useState, useMemo } from "react";
import { Dialog, Box, Grid } from "@mui/material";
import cn from "classnames";
import { observer } from "mobx-react";
import { Button } from "src/shared/ui/Button";
import { useLocale } from "src/providers/LocaleProvider";
import { GameTimeValues, GameTypeNew } from "src/store/lobbyStore";
import { bets, freeBets, matches as matchCounts } from "src/store/gameStore/constants/bets";
import { useStyles } from "./styles";
import filterFilledIcon from "src/assets/icons/filterFilledIcon.svg";
import closeIcon from "src/assets/icons/close.svg";
import time2mIcon from "src/assets/icons/ultraBulllet.svg";
import time3mIcon from "src/assets/icons/Bullet.svg";
import time5mIcon from "src/assets/icons/flash.svg";
import time10mIcon from "src/assets/icons/rabbit.svg";
import time20mIcon from "src/assets/icons/snail.svg";
import coinIcon from "src/assets/icons/coin.svg";
import flagIcon from "src/assets/icons/matchIcon.svg";
import arrowIcon from "src/assets/icons/lightArrow.svg";

export interface GameInviteFilters {
    bet: Record<number, boolean>;          
    time: Record<number, boolean>;         
    type: Record<string, boolean>;         
    gameMode: Record<number, boolean>;     
    matches: Record<number, boolean>;      
    showInvites: boolean;                  
    showGames: boolean;                    
}

interface Props {
    open: boolean;
    onClose: () => void;
    filters: GameInviteFilters;
    onApply: (filters: GameInviteFilters) => void;
    onReset?: () => void;
}

const GameAndInviteFilter = observer(({ open, onClose, filters, onApply, onReset }: Props) => {
    const classes = useStyles();
    const {
        lobby: {
            createGame: {
                buttons: {
                    create,
                    filter,
                    resetFilter,
                    applyFilter,
                    min,
                    potLimit,
                    classic,
                    match,
                    betTitle,
                    timeTitle,
                    typeTitle,
                    modeTitle,
                    matchTitle
                },
            },
        },
    } = useLocale();

    const isMobile = window.innerWidth <= 600;

    const [localFilters, setLocalFilters] = useState<GameInviteFilters>(filters);
    const [showMatchSelector, setShowMatchSelector] = useState(() => {
        return localFilters.gameMode[2] || false;
    });
    const [showAllBetsMobile, setShowAllBetsMobile] = useState(false);

    const isFreePlay = document.location.href.indexOf("lobby-free-play") > 0;
    const betList = isFreePlay ? freeBets : bets;

    const updateFilters = (section: keyof GameInviteFilters, key: string | number, value: boolean) => {
        setLocalFilters(prev => ({
            ...prev,
            [section]: {
                ...(prev[section] as Record<string | number, boolean>),
                [key]: value
            }
        }));
    };

    const times = useMemo(() => [
        { value: GameTimeValues["2m"], label: `2 ${min("min")}`, icon: time2mIcon },
        { value: GameTimeValues["3m"], label: `3 ${min("min")}`, icon: time3mIcon },
        { value: GameTimeValues["5m"], label: `5 ${min("min")}`, icon: time5mIcon },
        { value: GameTimeValues["10m"], label: `10 ${min("min")}`, icon: time10mIcon },
        { value: GameTimeValues["20m"], label: `20 ${min("min")}`, icon: time20mIcon }
    ], [min]);

    const types = [
        { value: GameTypeNew.classic, label: "Classic" },
        { value: GameTypeNew.nineHundredSixty, label: "960" }
    ];

    const handleGameModeChange = (mode: number) => {
        const isSelected = localFilters.gameMode[mode];

        updateFilters('gameMode', mode, !isSelected);

        if (mode === 2) { // Match 
            setShowMatchSelector(!isSelected ? true : false);
        } else if (mode === 1) { // Pot Limit 
            if (localFilters.gameMode[2] && !localFilters.gameMode[1]) {
                setShowMatchSelector(false);
            }
        }
    };

    const handleReset = () => {
        const resetFilters: GameInviteFilters = {
            bet: {},
            time: {},
            type: {},
            gameMode: {},
            matches: {},
            showInvites: true,
            showGames: true
        };
        setLocalFilters(resetFilters);
        setShowMatchSelector(false);
        if (onReset) {
            onReset();
        }
    };

    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    const handleClose = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} className={classes.mainLobbyFilterContainer}>
            <div className={classes.modalContent}>
                <div className={classes.modalTitleBlock}>
                    <p className={classes.modalTitle}>
                        <img src={filterFilledIcon} className={classes.filterIcon} alt="Filter" />
                        {filter('Filter')} 
                    </p>
                    <img src={closeIcon} onClick={handleClose} className={classes.closeIcon} alt="Close" />
                </div>

                <div className={classes.filterSection}>
                    {/* Ставки */}
                    <div className={classes.sectionHeader}> 
                        <img src={coinIcon} className={classes.headerCoinIcon} alt="coin" />
                        {betTitle('Bet')}
                    </div>

                    <div className={classes.betSection}>
                        {betList
                            .filter(amount => !isMobile || showAllBetsMobile || amount <= 200)
                            .map((amount, index) => (
                                <div
                                    key={amount}
                                    className={cn(classes.betItem, {
                                        [classes.betItemFull]: index === betList.length - 1,
                                        [classes.selected]: localFilters.bet[amount],
                                    })}
                                    onClick={() => updateFilters("bet", amount, !localFilters.bet[amount])}
                                >
                                    <div className={cn(classes.betItemContent, {
                                        [classes.centeredContent]: index === betList.length - 1,
                                    })}>
                                        <span className={localFilters.bet[amount] ? classes.goldText : classes.grayText}>
                                            {amount}
                                        </span>
                                    </div>
                                </div>
                        ))}
                    </div>
                    {isMobile && (
                        <div className={classes.betToggleMobile} onClick={() => setShowAllBetsMobile(prev => !prev)}>
                            <img
                                src={arrowIcon}
                                alt="toggle"
                                className={cn(classes.arrowIcon, {
                                    [classes.arrowIconRotated]: showAllBetsMobile, 
                                })}
                            />
                        </div>
                    )}

                    {/* Время */}
                    <div className={classes.sectionHeader}>
                        {timeTitle('Time')}
                    </div>

                    <div className={classes.timeSection}>
                        {times.map((timeItem) => (
                            <div
                                key={timeItem.value}
                                className={cn(classes.timeItem, {
                                    [classes.selected]: localFilters.time[timeItem.value],
                                })}
                                onClick={() =>
                                    updateFilters('time', timeItem.value, !localFilters.time[timeItem.value])
                                }
                            >
                                <img src={timeItem.icon} alt="" className={classes.timeIcon} />
                                <span>{timeItem.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Тип игры */}
                    <div className={classes.sectionHeader}>
                        {typeTitle('Type')}
                    </div>

                    <div className={classes.typeSection}>
                        {types.map((typeItem) => (
                            <div
                                key={typeItem.value}
                                className={cn(classes.typeItem, {
                                    [classes.selected]: localFilters.type[typeItem.value],
                                })}
                                onClick={() => updateFilters('type', typeItem.value, !localFilters.type[typeItem.value])}
                            >
                                <span>{typeItem.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Режим игры */}
                    {!isFreePlay && (
                         <>
                            <div className={classes.sectionHeader}>
                                {modeTitle('Mode')}
                            </div>

                            <div className={classes.gameModeSection}>
                                <div
                                    className={cn(classes.gameModeItem, {
                                        [classes.selected]: localFilters.gameMode[1],
                                    })}
                                    onClick={() => handleGameModeChange(1)}
                                >
                                    {potLimit("Pot Limit")}
                                </div>
                                <div
                                    className={cn(classes.gameModeItem, {
                                        [classes.selected]: localFilters.gameMode[2],
                                    })}
                                    onClick={() => handleGameModeChange(2)}
                                >
                                    {match("Match")}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Количество матчей */}
                    {showMatchSelector && (
                         <>
                            <div className={classes.sectionHeader}>
                                {matchTitle('Match')}
                            </div>

                            <div className={cn(classes.matchSectionWrapper, {
                              [classes.matchSectionWrapperOpen]: showMatchSelector,
                            })}>
                                <div className={cn(classes.matchSectionWrapper, {
                                    [classes.matchSectionWrapperOpen]: showMatchSelector,
                                })}>
                                    <div className={cn(classes.matchSection, classes.matchSectionAnimated)}>
                                        {matchCounts.map((mc, index) => (
                                            <div
                                                key={mc}
                                                className={cn(classes.matchItem, {
                                                    [classes.selected]: localFilters.matches[mc],
                                                })}
                                                onClick={() => updateFilters('matches', mc, !localFilters.matches[mc])}
                                                style={{ animationDelay: `${index * 80}ms` }}
                                            >
                                                <img src={flagIcon} alt="flag" style={{ width: 14, height: 14, marginRight: 6 }} />
                                                <span className={localFilters.matches[mc] ? classes.whiteText : classes.grayText}>
                                                    {mc}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Кнопки */}
                <div className={classes.buttonBlock}>
                    <Button
                        text={resetFilter("Сбросить")}
                        variant="outlined"
                        onClick={handleReset}
                        className={`${classes.button} ${classes.resetButton}`}
                    />
                    <Button
                        text={applyFilter("Применить")}
                        variant="contained"
                        onClick={handleApply}
                        className={`${classes.button} ${classes.applyButton}`}
                    />
                </div>
            </div>
        </Dialog>
    );
});

export default GameAndInviteFilter;